/**
 * Hoja de estilos única del widget, inyectada al Shadow DOM por el wrapper Lit.
 * Los componentes Svelte usan clases `ss-*`; no llevan bloques <style> porque
 * Svelte los inyectaría en el <head> del documento anfitrión (fuera del shadow).
 */
export const widgetStyles = /* css */ `
	:host {
		all: initial;
		display: block;
		font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
		color: #18181b;
		--ss-primary: #4f46e5;
		--ss-primary-hover: color-mix(in srgb, var(--ss-primary) 85%, #000);
		--ss-primary-text: #ffffff;
		--ss-surface: #ffffff;
		--ss-muted: #71717a;
		--ss-border: #e4e4e7;
		--ss-hover: #f4f4f5;
		--ss-radius: 14px;
		--ss-max-width: 420px;
	}

	/* Tema oscuro: <selfie-studio dark> */
	:host([dark]) {
		color: #fafafa;
		--ss-surface: #18181b;
		--ss-muted: #a1a1aa;
		--ss-border: #27272a;
		--ss-hover: #27272a;
	}

	*, *::before, *::after { box-sizing: border-box; }

	.ss-widget {
		max-width: var(--ss-max-width);
		margin: 0 auto;
		background: var(--ss-surface);
		border: 1px solid var(--ss-border);
		border-radius: var(--ss-radius);
		overflow: hidden;
		box-shadow: 0 8px 30px rgb(0 0 0 / 0.08);
	}

	/* Fondo transparente: <selfie-studio translucent> */
	:host([translucent]) .ss-widget {
		background: transparent;
		border-color: transparent;
		box-shadow: none;
	}
	:host([translucent]) .ss-header,
	:host([translucent]) .ss-footer {
		border-color: transparent;
	}

	/* Ocultar branding: <selfie-studio hide-branding> */
	:host([hide-branding]) .ss-footer { display: none; }

	.ss-header {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 12px 16px;
		border-bottom: 1px solid var(--ss-border);
	}
	.ss-header img { height: 24px; max-width: 110px; object-fit: contain; }
	.ss-header .ss-title { font-size: 14px; font-weight: 600; }
	.ss-header .ss-subtitle { font-size: 11px; color: var(--ss-muted); }

	.ss-body { padding: 16px; }

	/* ── Loader / engagement ─────────────────────────────────────────────── */
	.ss-loader {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 14px;
		padding: 40px 24px;
		text-align: center;
	}
	.ss-spinner {
		width: 38px;
		height: 38px;
		border-radius: 50%;
		border: 3px solid var(--ss-border);
		border-top-color: var(--ss-primary);
		animation: ss-spin 0.9s linear infinite;
	}
	@keyframes ss-spin { to { transform: rotate(360deg); } }

	.ss-loader h3 { margin: 0; font-size: 15px; }
	.ss-tip {
		font-size: 12.5px;
		color: var(--ss-muted);
		line-height: 1.5;
		min-height: 38px;
		animation: ss-fade 0.4s ease;
	}
	@keyframes ss-fade { from { opacity: 0; transform: translateY(4px); } }

	.ss-progress {
		width: 100%;
		height: 5px;
		border-radius: 999px;
		background: var(--ss-border);
		overflow: hidden;
	}
	.ss-progress > div {
		height: 100%;
		background: var(--ss-primary);
		border-radius: 999px;
		transition: width 0.3s ease;
	}

	/* ── Galería de plantillas ───────────────────────────────────────────── */
	.ss-gallery {
		display: grid;
		grid-template-columns: repeat(var(--ss-gallery-cols, 2), 1fr);
		gap: 10px;
	}

	/* Modo carrusel: <selfie-studio gallery="carousel"> */
	.ss-gallery.ss-carousel {
		display: flex;
		overflow-x: auto;
		scroll-snap-type: x mandatory;
		padding-bottom: 6px;
		scrollbar-width: thin;
	}
	.ss-gallery.ss-carousel .ss-template {
		flex: 0 0 58%;
		scroll-snap-align: center;
	}
	.ss-template {
		position: relative;
		aspect-ratio: 3 / 4;
		border-radius: 10px;
		border: 2px solid var(--ss-border);
		overflow: hidden;
		cursor: pointer;
		padding: 0;
		background: none;
		transition: border-color 0.15s ease, transform 0.15s ease;
	}
	.ss-template:hover { border-color: var(--ss-primary); transform: translateY(-2px); }
	:host([dark]) .ss-template { background: #09090b; }
	.ss-template .ss-template-bg { position: absolute; inset: 0; }
	.ss-template img.ss-bg-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
	.ss-template svg.ss-silhouette {
		position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);
		width: 75%; color: rgb(0 0 0 / 0.25);
	}
	.ss-template img.ss-wm { position: absolute; object-fit: contain; }
	.ss-template .ss-template-name {
		position: absolute; bottom: 0; left: 0; right: 0;
		padding: 6px 8px;
		font-size: 11px;
		font-weight: 600;
		color: #fff;
		background: linear-gradient(transparent, rgb(0 0 0 / 0.55));
		text-align: left;
	}

	/* ── Cámara ──────────────────────────────────────────────────────────── */
	.ss-camera { display: flex; flex-direction: column; gap: 12px; }
	.ss-video-wrap {
		position: relative;
		aspect-ratio: 3 / 4;
		border-radius: 10px;
		overflow: hidden;
		background: #09090b;
	}
	.ss-video-wrap video, .ss-video-wrap canvas {
		width: 100%; height: 100%; object-fit: cover;
		transform: scaleX(-1); /* espejo selfie */
	}
	.ss-camera-error {
		padding: 24px;
		font-size: 13px;
		color: #dc2626;
		text-align: center;
	}

	/* ── Editor ──────────────────────────────────────────────────────────── */
	.ss-editor { display: flex; flex-direction: column; gap: 12px; }
	.ss-canvas-wrap {
		border-radius: 10px;
		overflow: hidden;
		border: 1px solid var(--ss-border);
	}
	.ss-canvas-wrap canvas { display: block; width: 100%; height: auto; }

	.ss-controls {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}
	.ss-control-row { display: flex; align-items: center; gap: 8px; }
	.ss-control-row label {
		font-size: 11px;
		color: var(--ss-muted);
		width: 64px;
		flex-shrink: 0;
	}
	.ss-control-row input[type='range'] { flex: 1; accent-color: var(--ss-primary); }

	.ss-dpad {
		display: grid;
		grid-template-columns: repeat(3, 34px);
		grid-template-rows: repeat(2, 34px);
		gap: 4px;
		justify-content: center;
	}

	/* ── Botones ─────────────────────────────────────────────────────────── */
	.ss-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		border: 1px solid var(--ss-border);
		background: var(--ss-surface);
		color: inherit;
		border-radius: 9px;
		padding: 9px 14px;
		font-size: 13px;
		font-weight: 500;
		cursor: pointer;
		transition: background 0.15s ease;
	}
	.ss-btn:hover { background: var(--ss-hover); }
	.ss-btn:disabled { opacity: 0.5; cursor: not-allowed; }
	.ss-btn-primary {
		background: var(--ss-primary);
		border-color: var(--ss-primary);
		color: var(--ss-primary-text);
	}
	.ss-btn-primary:hover { background: var(--ss-primary-hover); }
	.ss-btn-icon { padding: 0; width: 34px; height: 34px; font-size: 15px; }
	.ss-btn-block { width: 100%; }
	.ss-btn-capture {
		width: 64px; height: 64px;
		border-radius: 50%;
		border: 4px solid #fff;
		background: var(--ss-primary);
		cursor: pointer;
		position: absolute;
		bottom: 14px; left: 50%;
		transform: translateX(-50%);
		box-shadow: 0 2px 12px rgb(0 0 0 / 0.4);
	}

	.ss-actions { display: flex; gap: 8px; }
	.ss-actions .ss-btn { flex: 1; }

	/* ── Error ───────────────────────────────────────────────────────────── */
	.ss-error {
		padding: 32px 20px;
		text-align: center;
		display: flex;
		flex-direction: column;
		gap: 12px;
		align-items: center;
	}
	.ss-error p { margin: 0; font-size: 13px; color: #dc2626; }

	.ss-footer {
		padding: 8px 16px;
		font-size: 10px;
		color: var(--ss-muted);
		text-align: center;
		border-top: 1px solid var(--ss-border);
	}
`;
